import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `En Chile, el 58% de las niñas y niños reside en hogares con seguridad alimentaria, mientras que un 21% experimenta inseguridad leve y el 21% restante, 
          inseguridad moderada a severa.

  Al analizar la evolución histórica, se observa que tras el deterioro registrado entre 2020 y 2022, el año 2024 muestra una mejora en la seguridad alimentaria. 
          Sin embargo, las cifras aún no recuperan los niveles pre pandemia (2017), una brecha que se explica principalmente por la persistencia de la inseguridad leve.

  Por su parte, la inseguridad moderada-severa, que alcanzó su máximo histórico del 25% en 2020, ha descendido al 21% en 2024, situándose 3 puntos porcentuales por 
          debajo de la medición de 2022.`,
    link: "https://public.flourish.studio/visualisation/20936053/embed",
  },
  {
    id: "graph2",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `No se observan mayores diferencias en la inseguridad alimentaria según el sexo de las niñas y niños. Para conocer más detalladamente la realidad 
          alimentaria y sus diferencias por género sería necesario contar con levantamientos a nivel individual, preguntando directamente a niñas y niños sus 
          experiencias y no sólo a nivel de hogar como el presente indicador.`,
    link: "https://public.flourish.studio/visualisation/20936057/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `No se observan diferencias estadísticamente significativas en la proporción de niñas y niños en hogares con inseguridad alimentaria según el tramo 
          etario. Para conocer más detalladamente la realidad alimentaria y sus diferencias por edad sería necesario contar con levantamientos a nivel individual, 
          preguntando directamente a niñas y niños sus experiencias.`,
    link: "https://public.flourish.studio/visualisation/20936062/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `A lo largo de la serie de tiempo, se observa que tanto las niñas y los niños que nacieron en Chile como los que nacieron fuera del país han tenido 
          tendencias similares: hubo un aumento en la proporción que vive en hogares con inseguridad alimentaria entre 2017 y 2020 y posteriormente hubo una 
          disminución. Sin embargo, entre las niñas y niños que nacieron fuera del país el aumento entre 2017 y 2020 fue de casi 20 puntos porcentuales (frente 
          a 5 de las niñas y niños nacidos en Chile) y en 2024 continúa habiendo una diferencia de 12 puntos.`,
    link: "https://public.flourish.studio/visualisation/20936065/embed",
  },
  {
    id: "graph5",
    title: "Análisis por pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `En todos los años medidos, existe una mayor proporción de niñas y niños que se identifican con algún pueblo originario viviendo en hogares con 
          inseguridad alimentaria, en comparación con aquellos que no lo hacen. La mayor diferencia se observó en 2020, cuando el 34% de las niñas y niños 
          indígena vivía en inseguridad alimentaria moderada-severa, frente al 24% de la no indígena.

  Además, tras la pandemia, la recuperación ha sido más lenta para las niñas y niños que se identifican con algún pueblo originario y viven en situación de 
        inseguridad moderada-severa, en contraste con quienes no se identifican.`,
    link: "https://public.flourish.studio/visualisation/20936074/embed",
  },
  {
    id: "graph6",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `Aunque los ingresos y la inseguridad alimentaria no siempre están asociados,1 en Chile existe una asociación fuerte entre el quintil de ingreso 
          en que se encuentra el hogar y experimentar inseguridad alimentaria. En 2024, un 36% de las niñas y niños que residen en hogares en el primer quintil, 
          es decir, el de menores ingresos, experimenta inseguridad alimentaria moderada-severa, frente a un 2% de las niñas y niños del quinto quintil. 

  Los quintiles de mayores ingresos, además, tuvieron un menor aumento de la inseguridad alimentaria en el año 2022, manteniéndose más estable que los quintiles 
        más bajos.`,
    link: "https://public.flourish.studio/visualisation/20936079/embed",
  },
  {
    id: "graph7",
    title: "Análisis por estructura familiar",
    menuTitle: "\u2022 Estructura familiar",
    text: `En todos los años de la serie, las niñas y niños que residen en hogares nucleares monoparentales presentan una mayor proporción de inseguridad alimentaria 
          moderada-severa, mientras que quienes viven en hogares nucleares biparentales registran la menor tasa.

  En 2024, mientras que un 30% de las niñas y niños en hogares monoparentales experimentan inseguridad alimentaria, la cifra desciende al 17% en los hogares biparentales.`,
    link: "https://public.flourish.studio/visualisation/27215189/embed",
  },
  {
    id: "graph8",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `En Chile, no hay diferencias estadísticamente significativas entre niñas y niños que residen en zonas urbanas y rurales en cuanto a la proporción 
          que vive en hogares con inseguridad alimentaria. En general, ambos grupos presentan proporciones similares de inseguridad alimentaria moderada-severa. 
          La única excepción se dio en 2017, cuando el 16% de las niñas y los niños en zonas rurales vivía en hogares con inseguridad alimentaria moderada o 
          severa, frente al 20% en zonas urbanas. Desde el año 2020, esta diferencia se ha igualado.`,
    link: "https://public.flourish.studio/visualisation/20936068/embed",
  },
  {
    id: "graph9",
    title: "Análisis por región",
    menuTitle: "\u2022 Región",
    text: `En 2024, las regiones tienen una distribución más similar en la proporción de niñas y niños que viven en hogares con inseguridad alimentaria que 
          en años anteriores. La región que presenta una mayor proporción es la Metropolitana (23%) seguida muy de cerca por Antofagasta (22%), Atacama (22%), 
          Valparaíso (22%) y Los Ríos (22%). Magallanes (14%), Aysén (16%) y Coquimbo (16%), por otro lado, son las regiones que presentan una menor proporción.`,
    link: "https://public.flourish.studio/visualisation/20936080/embed",
  }
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
            key={graph.id}
            onClick={() => handleMenuItemClick(graph.id)}
            style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
          >
            {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;